/**
 * Catering Packages saga: UI → dispatch(request) → saga → services/cateringPackagesApi → put(success|failure).
 * Mutations refetch the list silently afterwards (and on failure, to reconcile optimistic removals/reorders).
 * The details / edit screen reads ONE package from the dedicated `GET /packages/:id`, never from the list.
 */
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import type { SagaIterator } from '@redux-saga/types';
import { CPK } from './cateringPackagesTypes';
import type { PackageFailure, PackageImageChange, PackageSavePayload, PackageVariantDraft } from './cateringPackagesTypes';
import * as A from './cateringPackagesActions';
import { getCredentials } from './cateringCredentials';
import {
  createPackageApi,
  deletePackageApi,
  duplicatePackageApi,
  getPackageApi,
  listPackageCatalogItemsApi,
  listPackagesApi,
  removePackageImageApi,
  updatePackageApi,
  uploadPackageImageApi,
} from '../../services/cateringPackagesApi';
import type { CreatePackageBody, PackageVariantInput, UpdatePackageBody } from '../../services/cateringPackagesApi';
import type { CateringApiError } from '../../services/cateringApi';
import type { CateringPackage, PackageCatalogItem } from '../../components/catering/data/types';
import { toPackageItemInputs } from '../../components/catering/utils/packageItems';

const NO_LOCATION: PackageFailure = { message: 'No locationId available' };

function toFailure(e: unknown, fallback: string): PackageFailure {
  const err = (e ?? {}) as Partial<CateringApiError>;
  return {
    message: e instanceof Error && e.message ? e.message : fallback,
    ...(err.code ? { code: err.code } : {}),
    ...(err.status ? { status: err.status } : {}),
    ...(err.details ? { details: err.details } : {}),
  };
}

/**
 * Variant drafts → wire. The list is a whole-set replace, so EVERY variant goes out each time, with an
 * explicit `isDefault` (the BE would otherwise make the first one the default) and `sortOrder` = position.
 * `name` is always a string: blank stores null server-side (and clears a previously saved name).
 * `packageItems`: omitted keeps the variant's items, a list (even `[]`) replaces them.
 */
export function toVariantInputs(drafts: readonly PackageVariantDraft[]): PackageVariantInput[] {
  return drafts.map((v, sortOrder) => ({
    ...(v.id ? { id: v.id } : {}),
    name: (v.name ?? '').trim(),
    serves: v.serves.trim(),
    price: v.price,
    isDefault: v.isDefault,
    sortOrder,
    ...(v.packageItems !== undefined ? { packageItems: toPackageItemInputs(v.packageItems) } : {}),
    // The list is a full set, so each option's notes go out every time (null = none / cleared).
    notes: v.notes?.trim() || null,
  }));
}

/** Body fields shared by create + update, only including what the caller actually set. */
function saveFields(d: PackageSavePayload) {
  return {
    ...(d.name !== undefined ? { name: d.name.trim() } : {}),
    ...(d.notes !== undefined ? { notes: d.notes } : {}),
    ...(d.minQtyRequired !== undefined ? { minQtyRequired: d.minQtyRequired } : {}),
    ...(d.maxQtyRequired !== undefined ? { maxQtyRequired: d.maxQtyRequired } : {}),
    ...(d.sortOrder !== undefined ? { sortOrder: d.sortOrder } : {}),
    // Whole-set replaces: `[]` is meaningful for tags ("no tags"), so only skip when undefined.
    ...(d.tagIds !== undefined ? { tagIds: d.tagIds } : {}),
    ...(d.variants !== undefined ? { variants: toVariantInputs(d.variants) } : {}),
  };
}

const DATE_KEYS = ['orderStartingDate', 'packageAvailabilityFrom', 'packageAvailabilityTo'] as const;

/** PATCH dates: a value sets it, `null` clears it (no limit), omitted = unchanged. */
function patchDateFields(d: PackageSavePayload): Partial<Record<(typeof DATE_KEYS)[number], string | null>> {
  const out: Partial<Record<(typeof DATE_KEYS)[number], string | null>> = {};
  for (const key of DATE_KEYS) if (d[key] !== undefined) out[key] = d[key] as string | null;
  return out;
}

/** POST dates: only set values (the BE takes optional strings on create, not `null`). */
function createDateFields(d: PackageSavePayload): Partial<Record<(typeof DATE_KEYS)[number], string>> {
  const out: Partial<Record<(typeof DATE_KEYS)[number], string>> = {};
  for (const key of DATE_KEYS) {
    const v = d[key];
    if (typeof v === 'string' && v) out[key] = v;
  }
  return out;
}

/**
 * The image step, run AFTER the package itself has saved (so a rejected save never touches the image, and a
 * failed create can't leave an orphan upload). Uploads the new file to `packageId`, THEN removes the replaced
 * media — if the upload fails the old image is still there. Returns a user-facing message on failure,
 * `undefined` on success. Never throws: the package is already saved.
 */
const hasImageWork = (image: PackageImageChange | undefined): boolean => !!image && (!!image.file || !!image.removeMediaIds?.length);

function* applyImageChange(packageId: string, image: PackageImageChange | undefined, token: string | null): Generator<unknown, string | undefined, unknown> {
  if (!image || !hasImageWork(image)) return undefined;
  try {
    if (image.file) yield call(uploadPackageImageApi, image.file, packageId, token);
  } catch (e) {
    return `Package saved, but the image couldn't be uploaded: ${toFailure(e, 'upload failed').message}`;
  }
  try {
    for (const mediaId of image.removeMediaIds ?? []) yield call(removePackageImageApi, mediaId, token);
  } catch (e) {
    return `Package saved, but the previous image couldn't be removed: ${toFailure(e, 'remove failed').message}`;
  }
  return undefined;
}

/**
 * After an image upload / remove the package's `mediaResponseList` has changed, but the save response (POST / PATCH)
 * predates it. Re-read the package with the details API so the screen and the list see the new image; if that call
 * fails the save response is kept (the package IS saved — only its media list may be a step behind).
 */
function* reloadAfterImageChange(saved: CateringPackage, locationId: string, token: string | null): Generator<unknown, CateringPackage, unknown> {
  try {
    const fresh = (yield call(getPackageApi, saved.id, locationId, token)) as CateringPackage | undefined;
    return fresh ?? saved;
  } catch {
    return saved;
  }
}

function* refetchSilently(): SagaIterator {
  yield put(A.fetchPackagesRequest({ silent: true }));
}

// ── Fetch ───────────────────────────────────────────────────────────────────────

function* fetchWorker(): SagaIterator {
  try {
    const { locationId, token } = getCredentials();
    if (!locationId) {
      yield put(A.fetchPackagesFailure(NO_LOCATION.message));
      return;
    }
    const packages: CateringPackage[] = yield call(listPackagesApi, locationId, token);
    yield put(A.fetchPackagesSuccess({ packages, locationId }));
  } catch (e) {
    yield put(A.fetchPackagesFailure(toFailure(e, 'Failed to load packages').message));
  }
}

/** The details / edit screen: `GET /packages/:id?locationId=`. A 404 / `NOT_FOUND` is reported as "not found", not as a failure to retry. */
function* fetchDetailWorker(action: ReturnType<typeof A.fetchPackageDetailRequest>): SagaIterator {
  const { id } = action.payload;
  try {
    const { locationId, token } = getCredentials();
    if (!locationId) {
      yield put(A.fetchPackageDetailFailure({ id, message: NO_LOCATION.message }));
      return;
    }
    const pkg: CateringPackage = yield call(getPackageApi, id, locationId, token);
    yield put(A.fetchPackageDetailSuccess(pkg));
  } catch (e) {
    const failure = toFailure(e, 'Failed to load the package');
    yield put(A.fetchPackageDetailFailure({ id, message: failure.message, notFound: failure.code === 'NOT_FOUND' || failure.status === 404 }));
  }
}

function* fetchCatalogItemsWorker(): SagaIterator {
  try {
    const { locationId, token } = getCredentials();
    if (!locationId) {
      yield put(A.fetchPackageCatalogItemsFailure(NO_LOCATION.message));
      return;
    }
    const items: PackageCatalogItem[] = yield call(listPackageCatalogItemsApi, locationId, token);
    yield put(A.fetchPackageCatalogItemsSuccess(items, locationId));
  } catch (e) {
    yield put(A.fetchPackageCatalogItemsFailure(toFailure(e, 'Failed to load catalogue items').message));
  }
}

// ── Packages ────────────────────────────────────────────────────────────────────

function* createPackageWorker(action: ReturnType<typeof A.createPackageRequest>): SagaIterator {
  try {
    const { locationId, token } = getCredentials();
    if (!locationId) {
      yield put(A.createPackageFailure(NO_LOCATION));
      return;
    }
    const d = action.payload;
    const body: CreatePackageBody = {
      locationId,
      ...saveFields(d),
      ...createDateFields(d),
      name: d.name.trim(),
      variants: toVariantInputs(d.variants),
    };
    let pkg: CateringPackage = yield call(createPackageApi, body, token);
    // The image goes to the id the BE just returned, and `saving` stays true until it is done so the form
    // reacts once, with the whole outcome.
    const imageError: string | undefined = yield call(applyImageChange, pkg.id, d.image, token);
    if (hasImageWork(d.image)) pkg = yield call(reloadAfterImageChange, pkg, locationId, token);
    yield put(A.createPackageSuccess(pkg, imageError));
    yield call(refetchSilently);
  } catch (e) {
    yield put(A.createPackageFailure(toFailure(e, 'Failed to create package')));
  }
}

function* updatePackageWorker(action: ReturnType<typeof A.updatePackageRequest>): SagaIterator {
  try {
    const { locationId, token } = getCredentials();
    if (!locationId) {
      yield put(A.updatePackageFailure(NO_LOCATION));
      return;
    }
    const data = action.payload.data;
    const body: UpdatePackageBody = { locationId, ...saveFields(data), ...patchDateFields(data) };
    let pkg: CateringPackage = yield call(updatePackageApi, action.payload.id, body, token);
    const imageError: string | undefined = yield call(applyImageChange, action.payload.id, data.image, token);
    if (hasImageWork(data.image)) pkg = yield call(reloadAfterImageChange, pkg, locationId, token);
    yield put(A.updatePackageSuccess(pkg, imageError));
    yield call(refetchSilently);
  } catch (e) {
    yield put(A.updatePackageFailure(toFailure(e, 'Failed to update package')));
  }
}

function* duplicatePackageWorker(action: ReturnType<typeof A.duplicatePackageRequest>): SagaIterator {
  try {
    const { locationId, token } = getCredentials();
    if (!locationId) {
      yield put(A.duplicatePackageFailure(NO_LOCATION));
      return;
    }
    const name = action.payload.name?.trim();
    const pkg: CateringPackage = yield call(
      duplicatePackageApi,
      action.payload.id,
      { locationId, ...(name ? { name } : {}) },
      token,
    );
    yield put(A.duplicatePackageSuccess(pkg));
    yield call(refetchSilently);
  } catch (e) {
    yield put(A.duplicatePackageFailure(toFailure(e, 'Failed to duplicate package')));
  }
}

function* deletePackageWorker(action: ReturnType<typeof A.deletePackageRequest>): SagaIterator {
  try {
    const { locationId, token } = getCredentials();
    if (!locationId) {
      yield put(A.deletePackageFailure(NO_LOCATION));
      yield call(refetchSilently);
      return;
    }
    yield call(deletePackageApi, action.payload.id, locationId, token);
    yield put(A.deletePackageSuccess(action.payload.id));
  } catch (e) {
    yield put(A.deletePackageFailure(toFailure(e, 'Failed to delete package')));
    yield call(refetchSilently);
  }
}

/** Persists a drag-reorder through the existing package PATCH (`sortOrder` only) — no dedicated endpoint. */
function* reorderWorker(action: ReturnType<typeof A.reorderPackagesRequest>): SagaIterator {
  const { changes } = action.payload;
  if (changes.length === 0) {
    yield put(A.reorderPackagesSuccess());
    return;
  }
  try {
    const { locationId, token } = getCredentials();
    if (!locationId) {
      yield put(A.reorderPackagesFailure(NO_LOCATION));
      yield call(refetchSilently);
      return;
    }
    yield all(changes.map((c) => call(updatePackageApi, c.id, { locationId, sortOrder: c.sortOrder }, token)));
    yield put(A.reorderPackagesSuccess());
  } catch (e) {
    yield put(A.reorderPackagesFailure(toFailure(e, 'Failed to save the new order')));
    yield call(refetchSilently);
  }
}

export function* cateringPackagesSaga(): SagaIterator {
  yield all([
    takeLatest(CPK.FETCH_REQUEST, fetchWorker),
    takeLatest(CPK.DETAIL_FETCH_REQUEST, fetchDetailWorker),
    takeLatest(CPK.CATALOG_FETCH_REQUEST, fetchCatalogItemsWorker),
    takeLatest(CPK.REORDER_REQUEST, reorderWorker),
    takeEvery(CPK.PACKAGE_CREATE_REQUEST, createPackageWorker),
    takeEvery(CPK.PACKAGE_UPDATE_REQUEST, updatePackageWorker),
    takeEvery(CPK.PACKAGE_DUPLICATE_REQUEST, duplicatePackageWorker),
    takeEvery(CPK.PACKAGE_DELETE_REQUEST, deletePackageWorker),
  ]);
}
